/**
 * Connection options for twreact's grouped `<Select>`.
 *
 * twreact's `grouped` mode does NOT read a `group` key off a flat option. It flattens with
 * `options.reduce((acc, g) => { g.options.forEach(...) })`, so every top-level entry must be
 * `{ label: <groupName>, options: [ { value, label }, … ] }`. Handing it a flat list threw
 * "Cannot read properties of undefined (reading 'forEach')" inside a useMemo and blanked the
 * whole agent detail page — but only once a company actually had a connection to render,
 * which is why it stayed hidden for two releases.
 *
 * Pure and separate so the shape the component demands is pinned by a test rather than
 * rediscovered from a stack trace in a minified vendor chunk.
 */

/** Connections with no category still have to appear — dropping them would silently hide a
 *  row the user can see on the Connections screen. */
const UNGROUPED_LABEL = 'Other';

export function connectionOptionLabel(connection) {
  const credentials = connection.has_credentials ? '' : ' — no credentials';
  return `${connection.name} (${connection.provider}${credentials})`;
}

/**
 * @param {Array} connections rows as returned by `crud AppConnection list`
 * @returns {Array} `[{ label, options: [{ value, label }] }]`, groups in first-seen order so
 *   the list matches the order the API returned rather than jumping around alphabetically.
 */
export function groupConnectionOptions(connections = []) {
  const groups = new Map();
  for (const connection of connections) {
    const key = connection.category_key || UNGROUPED_LABEL;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push({ value: connection.id, label: connectionOptionLabel(connection) });
  }
  return [...groups.entries()].map(([label, options]) => ({ label, options }));
}
